import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Save,
  Close
} from '@mui/icons-material';
import ApiContext from 'contexts/ApiContext';
import useApi from 'hooks/useApi';

// ============================================================================
// INTERFACES
// ============================================================================

interface DefaultStepsSettingsProps {
  teamUid: string;
  clientUID?: string;
}

interface FieldSettings {
  required?: boolean;
  multiple?: boolean;
  cameraFacing?: 'env' | 'user'; // CAMERA: 'env' = back, 'user' = front (selfie)
  selectAll?: boolean; // CHECKBOX: require EVERY option to be checked
  [key: string]: any;
}

interface Field {
  uid?: string;
  question: string;
  description: string;
  inputComponent: string;
  options: string[];
  // UI-only raw text for the "Options (comma-separated)" input, so a comma the
  // user just typed isn't stripped on every keystroke. Not persisted — dropped
  // by safeField (load) and the save-time sanitize.
  optionsText?: string;
  settings: FieldSettings;
  resourceType?: string;
}

type StepType = 'IMAGE' | 'FORM' | 'DOCUMENT' | 'ESIGN' | 'LOCATION' | 'INSTRUCTION';

interface InstructionSettings {
  instruction?: string;
  isEsignRequired?: boolean;
  supportfiles?: any[];
  uploadedDocument?: string;
  [k: string]: any;
}

interface RoleDefaultStep {
  uid?: string;
  roles: string[];
  title: string;
  stepType: StepType;
  fields: Field[];
  settings: InstructionSettings;
  order: number;
  required: boolean;
  enabled: boolean;
}

// ============================================================================
// CONSTANTS & HELPERS
// ============================================================================

// v1: default steps support only FORM (data capture) and INSTRUCTION (acknowledge).
const STEP_TYPES: StepType[] = ['FORM', 'INSTRUCTION'];

// Input components mirror the flow-builder field editor (FormComponents/index.tsx)
const INPUT_COMPONENTS = [
  { value: 'TEXT', label: 'TEXT' },
  { value: 'NUMBER', label: 'NUMBER' },
  { value: 'FILE', label: 'FILE' },
  { value: 'SELECT', label: 'DROPDOWN' },
  { value: 'CHECKBOX', label: 'CHECKBOX' },
  { value: 'RADIO', label: 'RADIO' },
  { value: 'CAMERA', label: 'CAMERA' },
  { value: 'DATE', label: 'DATE' },
  { value: 'ESIGN', label: 'ESIGN' },
  { value: 'SWITCH', label: 'TOGGLE' },
];

// Field types whose choices are defined via the options list
const OPTION_COMPONENTS = ['SELECT', 'RADIO', 'CHECKBOX'];

const emptyField = (): Field => ({
  question: '',
  description: '',
  inputComponent: 'TEXT',
  options: [],
  settings: { required: false },
});

const emptyStep = (): RoleDefaultStep => ({
  roles: [],
  title: '',
  stepType: 'FORM',
  fields: [],
  settings: {},
  order: 0,
  required: true,
  enabled: true,
});

const safeField = (f: any): Field => ({
  uid: f?.uid,
  question: f?.question || '',
  description: f?.description || '',
  inputComponent: f?.inputComponent || 'TEXT',
  options: Array.isArray(f?.options) ? f.options : [],
  settings: { required: false, ...(f?.settings || {}) },
  resourceType: f?.resourceType,
});

const safeStep = (s: any): RoleDefaultStep => ({
  uid: s?.uid,
  roles: Array.isArray(s?.roles) && s.roles.length
    ? s.roles.map((r: any) => (r?.id || r?._id || r)).filter(Boolean).map(String)
    : (s?.role ? [(s.role.id || s.role._id || s.role).toString()] : []),
  title: s?.title || '',
  stepType: STEP_TYPES.includes(s?.stepType) ? s.stepType : 'FORM',
  fields: Array.isArray(s?.fields) ? s.fields.map(safeField) : [],
  settings: s?.settings && typeof s.settings === 'object' ? s.settings : {},
  order: typeof s?.order === 'number' ? s.order : 0,
  required: s?.required !== undefined ? s.required : true,
  enabled: s?.enabled !== undefined ? s.enabled : true,
});

// ============================================================================
// COMPONENT
// ============================================================================

const DefaultStepsSettings: React.FC<DefaultStepsSettingsProps> = ({ teamUid, clientUID }) => {
  const apiContext = useContext(ApiContext);
  const { showToast } = useApi();
  const formRef = useRef<HTMLDivElement>(null);

  const [steps, setSteps] = useState<RoleDefaultStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Form state: null = no form, step object = editing/creating
  const [editingStep, setEditingStep] = useState<RoleDefaultStep | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null); // null = new, number = editing existing
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => { if (teamUid) loadData(); }, [teamUid]);

  const loadData = async () => {
    if (!apiContext || !teamUid) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        apiContext.getDefaultSteps(teamUid),
        apiContext.getResourceTypes(clientUID, 'user'),
      ]);
      const stepsRes = results[0].status === 'fulfilled' ? results[0].value : null;
      const stepsData = stepsRes?.data;
      const stepsArray = Array.isArray(stepsData) ? stepsData : Array.isArray(stepsRes) ? stepsRes : [];
      setSteps(stepsArray.map(safeStep).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));

      const roleData = results[1].status === 'fulfilled' ? results[1].value?.data : null;
      setRoles(Array.isArray(roleData) ? roleData : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // ==================== ACTIONS ====================

  const handleAdd = () => {
    setEditingStep(emptyStep());
    setEditingIndex(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleEdit = (index: number) => {
    setEditingStep({ ...steps[index], fields: steps[index].fields.map(safeField) });
    setEditingIndex(index);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleCancel = () => {
    setEditingStep(null);
    setEditingIndex(null);
  };

  const handleDelete = async (index: number) => {
    const step = steps[index];
    if (step.uid && apiContext) {
      try {
        await apiContext.deleteDefaultStep(teamUid, step.uid);
        showToast('Default step deleted', 'success');
      } catch (err) { showToast('Failed to delete', 'error'); }
    }
    const updated = steps.filter((_, i) => i !== index);
    setSteps(updated);
    if (editingIndex === index) handleCancel();
  };

  const handleSave = async () => {
    if (!apiContext || !editingStep) return;
    setSaving(true);
    try {
      let res: any;
      // Sanitize fields (drops the UI-only `optionsText`) before persisting.
      const payload = { ...editingStep, fields: editingStep.fields.map(safeField) };
      if (editingStep.uid) {
        res = await apiContext.updateDefaultStep(teamUid, editingStep.uid, payload);
      } else {
        res = await apiContext.createDefaultStep(teamUid, payload);
      }
      // Backend returns HTTP 200 with { success:false } on validation errors, so
      // axios doesn't throw — surface the failure instead of a false "saved".
      if (res && res.success === false) {
        showToast(typeof res.data === 'string' ? res.data : (res.message || 'Failed to save'), 'error');
        return;
      }
      const savedData = res?.data;
      const safe = safeStep(savedData?.uid ? savedData : { ...editingStep, uid: savedData?.uid || editingStep.uid });

      if (editingIndex !== null) {
        const updated = [...steps];
        updated[editingIndex] = safe;
        setSteps(updated.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      } else {
        setSteps([...steps, safe].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      }
      setEditingStep(null);
      setEditingIndex(null);
      showToast('Default step saved', 'success');
    } catch (err) {
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ==================== FORM STATE UPDATERS ====================

  const updateField = (updates: Partial<RoleDefaultStep>) => {
    if (!editingStep) return;
    setEditingStep({ ...editingStep, ...updates });
  };

  const updateStepField = (fieldIndex: number, updates: Partial<Field>) => {
    if (!editingStep) return;
    const fields = [...editingStep.fields];
    fields[fieldIndex] = { ...fields[fieldIndex], ...updates };
    setEditingStep({ ...editingStep, fields });
  };

  const updateFieldSettings = (fieldIndex: number, updates: Partial<FieldSettings>) => {
    if (!editingStep) return;
    const fields = [...editingStep.fields];
    fields[fieldIndex] = { ...fields[fieldIndex], settings: { ...fields[fieldIndex].settings, ...updates } };
    setEditingStep({ ...editingStep, fields });
  };

  const addStepField = () => {
    if (!editingStep) return;
    setEditingStep({ ...editingStep, fields: [...editingStep.fields, emptyField()] });
  };

  const removeStepField = (fieldIndex: number) => {
    if (!editingStep) return;
    setEditingStep({ ...editingStep, fields: editingStep.fields.filter((_, i) => i !== fieldIndex) });
  };

  // INSTRUCTION-step settings (instruction text / e-sign / uploaded document).
  const updateSettings = (updates: Partial<InstructionSettings>) => {
    if (!editingStep) return;
    setEditingStep({ ...editingStep, settings: { ...(editingStep.settings || {}), ...updates } });
  };

  const handleInstructionUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !apiContext || !editingStep) return;
    if (file.type !== 'application/pdf') { showToast('Only PDF files are allowed', 'error'); return; }
    setUploadingDoc(true);
    try {
      const resp: any = await apiContext.uploadAsset(file, 'instructions/documents');
      const u = resp?.data || resp;
      const fileData = {
        question: '', description: '', file_name: file.name,
        file_path: u?.file_path || u?.path || '',
        mime_type: file.type, public_url: u?.public_url || u?.url || '',
      };
      const existing = editingStep.settings?.supportfiles || [];
      updateSettings({ supportfiles: [...existing, fileData], uploadedDocument: file.name });
      showToast('Document uploaded', 'success');
    } catch {
      showToast('Upload failed', 'error');
    } finally {
      setUploadingDoc(false);
    }
  };

  // ==================== VALIDATION ====================

  const isFormValid = (): boolean => {
    if (!editingStep) return false;
    if (!editingStep.roles?.length) return false;
    if (!editingStep.title?.trim()) return false;
    if (editingStep.stepType === 'INSTRUCTION') {
      // INSTRUCTION steps need instruction text, not input fields.
      if (!editingStep.settings?.instruction?.trim()) return false;
    } else if (!editingStep.fields.every((f) => f.question?.trim())) {
      return false;
    }
    return true;
  };

  // ==================== HELPERS ====================

  const getRoleName = (roleId: string): string => {
    const rt = roles.find((r: any) => (r.id || r._id) === roleId || r.uid === roleId);
    return rt?.name || roleId;
  };

  // ==================== RENDER: FIELD ROW ====================

  const renderFieldRow = (fieldIndex: number, field: Field) => {
    const hasOptions = OPTION_COMPONENTS.includes(field.inputComponent);
    return (
      <Box key={fieldIndex} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
        <Chip label={fieldIndex + 1} size="small" variant="filled" color="primary"
          sx={{ fontWeight: 600, minWidth: 32, mt: 0.5 }} />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField size="small" sx={{ flex: 1, minWidth: 160 }} label="Question / Label"
              placeholder="e.g. Upload your selfie"
              value={field.question} onChange={(e) => updateStepField(fieldIndex, { question: e.target.value })} />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Field Type</InputLabel>
              <Select value={field.inputComponent} label="Field Type"
                onChange={(e) => updateStepField(fieldIndex, { inputComponent: e.target.value, options: [], optionsText: '' })}>
                {INPUT_COMPONENTS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {hasOptions && (
            <TextField size="small" fullWidth label="Options (comma-separated)"
              placeholder="Option A, Option B, Option C"
              value={field.optionsText ?? (field.options || []).join(', ')}
              onChange={(e) => updateStepField(fieldIndex, { optionsText: e.target.value, options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) })} />
          )}
          {field.inputComponent === 'CAMERA' && (
            <FormControl size="small" sx={{ minWidth: 200, alignSelf: 'flex-start' }}>
              <InputLabel>Camera</InputLabel>
              <Select value={field.settings?.cameraFacing || 'env'} label="Camera"
                onChange={(e) => updateFieldSettings(fieldIndex, { cameraFacing: e.target.value as 'env' | 'user' })}>
                <MenuItem value="env">Back camera</MenuItem>
                <MenuItem value="user">Front camera (selfie)</MenuItem>
              </Select>
            </FormControl>
          )}
          {field.inputComponent === 'CHECKBOX' && (
            <FormControlLabel
              control={<Switch size="small" checked={!!field.settings?.selectAll}
                onChange={(e) => updateFieldSettings(fieldIndex, { selectAll: e.target.checked })} />}
              label={<Typography variant="caption">Require all options checked</Typography>} sx={{ mr: 0 }} />
          )}
          <FormControlLabel
            control={<Switch size="small" checked={!!field.settings?.required}
              onChange={(e) => updateFieldSettings(fieldIndex, { required: e.target.checked })} />}
            label={<Typography variant="caption">Required</Typography>} sx={{ mr: 0 }} />
        </Box>
        <IconButton size="small" color="error" onClick={() => removeStepField(fieldIndex)} sx={{ flexShrink: 0, mt: 0.5 }}>
          <Delete fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  // ==================== MAIN RENDER ====================

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Default Steps</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Steps a user must complete (once per transaction) before their assigned workflow step, configured per role.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ==================== LIST TABLE ==================== */}
      <TableContainer component={Paper} elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Type</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Order</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Required</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {steps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No default steps configured
                </TableCell>
              </TableRow>
            ) : (
              steps.map((s, idx) => (
                <TableRow key={s.uid || idx} hover
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    ...(editingIndex === idx && { bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.50' } }),
                  }}>
                  <TableCell sx={{ py: 1.5, fontWeight: 500 }}>
                    {s.roles?.length
                      ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{s.roles.map((r) => <Chip key={r} size="small" variant="outlined" color="primary" label={getRoleName(r)} sx={{ fontSize: '0.7rem', height: 22 }} />)}</Box>
                      : <Typography component="span" color="text.disabled" sx={{ fontStyle: 'italic' }}>No role</Typography>}
                  </TableCell>
                  <TableCell sx={{ py: 1.5, fontWeight: 500 }}>{s.title || <Typography component="span" color="text.disabled" sx={{ fontStyle: 'italic' }}>Untitled</Typography>}</TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Chip size="small" label={s.stepType} sx={{ fontSize: '0.7rem', height: 22 }} />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5, color: 'text.secondary' }}>{s.order}</TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Chip label={s.required ? 'Required' : 'Optional'} size="small"
                      sx={{
                        fontWeight: 500, fontSize: '0.7rem', height: 22,
                        bgcolor: s.required ? 'warning.light' : 'grey.200',
                        color: s.required ? 'warning.dark' : 'text.secondary',
                      }} />
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Chip label={s.enabled ? 'Active' : 'Inactive'} size="small"
                      sx={{
                        fontWeight: 500, fontSize: '0.7rem', height: 22,
                        bgcolor: s.enabled ? 'success.light' : 'grey.200',
                        color: s.enabled ? 'success.dark' : 'text.secondary',
                      }} />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(idx)}
                        sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.50' } }}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(idx)}
                        sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.50' } }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ==================== FORM ==================== */}
      <div ref={formRef}>
        {editingStep ? (
          <Card variant="outlined" sx={{ border: '1px solid', borderColor: 'primary.main' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {editingIndex !== null ? 'Edit Default Step' : 'New Default Step'}
                </Typography>
                <IconButton size="small" onClick={handleCancel}><Close /></IconButton>
              </Box>

              {/* Role + Enabled */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2.5, alignItems: 'center' }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Role <span style={{ color: '#f44336' }}>*</span></InputLabel>
                  <Select multiple value={editingStep.roles || []} label="Role *"
                    onChange={(e) => updateField({ roles: typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]) })}
                    renderValue={(selected) => (selected as string[]).map((r) => getRoleName(r)).join(', ')}>
                    {roles.length === 0
                      ? <MenuItem disabled><em>No roles found</em></MenuItem>
                      : roles.map((rt: any) => (
                        <MenuItem key={rt.id || rt._id} value={rt.uid || rt.id || rt._id}>{rt.name}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Switch checked={editingStep.enabled} onChange={(e) => updateField({ enabled: e.target.checked })} />}
                  label="Enabled" sx={{ mr: 0 }} />
              </Box>

              {/* Title + Step Type + Order */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                <TextField size="small" label="Title" value={editingStep.title}
                  onChange={(e) => updateField({ title: e.target.value })}
                  placeholder="e.g. Identity Verification" sx={{ flex: 1, minWidth: 180 }}
                  required error={!editingStep.title.trim()} />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Step Type</InputLabel>
                  <Select value={editingStep.stepType} label="Step Type"
                    onChange={(e) => updateField({ stepType: e.target.value as StepType })}>
                    {STEP_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
                <Tooltip title="Completion order. When a role has multiple default steps, they are presented in ascending order — 0 first, then 1, 2, …">
                  <TextField size="small" type="number" label="Order" value={editingStep.order}
                    onChange={(e) => updateField({ order: parseInt(e.target.value, 10) || 0 })}
                    sx={{ width: 110 }} />
                </Tooltip>
              </Box>

              <Box sx={{ mb: 2.5 }}>
                <FormControlLabel
                  control={<Switch checked={editingStep.required} onChange={(e) => updateField({ required: e.target.checked })} />}
                  label={<Box>
                    <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>Required</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Required steps gate the workflow step until completed. Optional steps never block.
                    </Typography>
                  </Box>} />
              </Box>

              <Divider sx={{ mb: 2 }} />

              {editingStep.stepType === 'INSTRUCTION' ? (
                /* ===== INSTRUCTION: instruction text + e-sign + optional document ===== */
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>Instruction</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    The text shown to the user for this step.
                  </Typography>
                  <TextField fullWidth multiline minRows={3} size="small"
                    placeholder="Enter the instruction text that will be displayed to users..."
                    value={editingStep.settings?.instruction || ''}
                    onChange={(e) => updateSettings({ instruction: e.target.value })}
                    error={!editingStep.settings?.instruction?.trim()}
                    sx={{ mb: 2 }} />

                  <FormControlLabel
                    control={<Switch checked={!!editingStep.settings?.isEsignRequired}
                      onChange={(e) => updateSettings({ isEsignRequired: e.target.checked })} />}
                    label={<Typography variant="body2">E-Sign Required</Typography>}
                    sx={{ display: 'block', mb: 1 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Button variant="outlined" component="label" size="small" disabled={uploadingDoc}>
                      {uploadingDoc ? 'Uploading…' : 'Upload Document (PDF)'}
                      <input hidden type="file" accept="application/pdf" onChange={handleInstructionUpload} />
                    </Button>
                    {((editingStep.settings?.supportfiles?.length ?? 0) > 0) && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {(editingStep.settings?.supportfiles ?? []).map((f: any, i: number) => (
                          <Chip key={i} size="small" label={f.file_name || `Document ${i + 1}`}
                            onDelete={() => {
                              const next = (editingStep?.settings?.supportfiles ?? []).filter((_: any, j: number) => j !== i);
                              updateSettings({ supportfiles: next, uploadedDocument: next[0]?.file_name || '' });
                            }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                /* ===== FORM: input fields editor ===== */
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>Fields</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    The inputs the user fills in for this step. Add a field, set its label and type, and mark it required.
                  </Typography>
                  {editingStep.fields.length === 0 ? (
                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mb: 1 }}>
                      No fields yet.
                    </Typography>
                  ) : (
                    editingStep.fields.map((field, i) => renderFieldRow(i, field))
                  )}
                  <Button variant="text" size="small" startIcon={<Add />} onClick={addStepField} sx={{ mb: 2 }}>
                    Add Field
                  </Button>
                </>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
                <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || !isFormValid()}>
                  {saving ? 'Saving...' : editingIndex !== null ? 'Update Default Step' : 'Save Default Step'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Default Step
          </Button>
        )}
      </div>
    </Box>
  );
};

export default DefaultStepsSettings;
